import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Crown, CheckCircle, Users, Calendar } from 'lucide-react';
import { format } from 'date-fns';

export default function SessionSummaryView({ session, officers, onBack }) {
  const facilitator = officers.find((o) => o.user_email === session.facilitator);

  return (
    <div className="space-y-6">
      <Button data-testid="hc-back-to-sessions-btn" variant="ghost" onClick={onBack} className="text-slate-400 hover:text-slate-200">
        <ArrowLeft className="w-4 h-4 mr-2" /> Back to Sessions
      </Button>

      <div className="flex items-center gap-3">
        <Crown className="w-7 h-7 text-amber-400" />
        <div>
          <h2 className="text-xl font-bold text-white">
            Session — {session.session_date ? format(new Date(session.session_date), 'MMM d, yyyy') : 'Unknown'}
          </h2>
          <p className="text-sm text-slate-300 flex items-center gap-2">
            <Users className="w-3 h-3" /> {facilitator?.callsign || session.facilitator}
            <Badge className="bg-emerald-500/20 text-emerald-300 ml-2">Completed</Badge>
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        {[
        { label: 'Noise', value: session.noise_count || 0, color: 'text-slate-200' },
        { label: 'Signal', value: session.signal_count || 0, color: 'text-amber-300' },
        { label: 'Balloons', value: session.balloon_count || 0, color: 'text-sky-300' },
        { label: 'Rocks', value: session.rock_count || 0, color: 'text-white' }].
        map((s) =>
        <Card key={s.label} className="glass-panel border-slate-800">
            <CardContent className="bg-slate-800 p-4 text-center">
              <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
              <p className="text-xs text-slate-400">{s.label}</p>
            </CardContent>
          </Card>
        )}
      </div>

      {session.pattern_observations &&
      <Card className="glass-panel border-slate-800">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-indigo-400">Pattern Observations</CardTitle></CardHeader>
          <CardContent><p className="text-sm text-slate-300 whitespace-pre-wrap">{session.pattern_observations}</p></CardContent>
        </Card>
      }

      {session.upward_report &&
      <Card className="glass-panel border-slate-800">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-amber-400">Upward Report</CardTitle></CardHeader>
          <CardContent><p className="text-sm text-slate-300 whitespace-pre-wrap">{session.upward_report}</p></CardContent>
        </Card>
      }

      {session.action_items?.length > 0 &&
      <Card className="glass-panel border-slate-800">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-emerald-400">Action Items</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {session.action_items.map((item, i) => {
            const owner = officers.find((o) => o.user_email === item.owner);
            return (
              <div key={i} className="flex items-center gap-3 p-2 rounded bg-slate-800/40">
                  <CheckCircle className={`w-4 h-4 ${item.status === 'done' ? 'text-emerald-400' : 'text-slate-600'}`} />
                  <span className="text-sm text-slate-300 flex-1">{item.description}</span>
                  <span className="text-xs text-slate-400">{owner?.callsign || item.owner}</span>
                  {item.deadline && <span className="text-xs text-slate-400">{item.deadline}</span>}
                  <Badge className={item.status === 'done' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-amber-500/20 text-amber-300'}>
                    {item.status}
                  </Badge>
                </div>);

          })}
          </CardContent>
        </Card>
      }

      {session.next_session_date &&
      <Card className="glass-panel border-slate-800">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-cyan-400">Next Session</CardTitle></CardHeader>
          <CardContent>
            <p className="text-sm text-slate-300 flex items-center gap-2">
              <Calendar className="w-4 h-4 text-slate-400" />
              {format(new Date(session.next_session_date), 'MMM d, yyyy')}
            </p>
            {session.next_session_prework &&
          <p className="text-sm text-slate-300 mt-2">Pre-work: {session.next_session_prework}</p>
          }
          </CardContent>
        </Card>
      }
    </div>);

}